import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createContactTagConnectionV1,
  deleteContactTagConnectionV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIResponse, type Contact } from "@/lib/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { type ContactSubitemMutationOptions } from "./contact-subitem-mutation-options";

export const useCreateContactTagConnection = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createContactTagConnectionV1>[0]) =>
      createContactTagConnectionV1(data),
    onError: () => {
      toast.error("Error creating contact tag connection");
    },

    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.contact, variables.contactId],
      });
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact tag connection created successfully");
      }
    },
  });
};

export const useDeleteContactTagConnection = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteContactTagConnectionV1>[0]) =>
      deleteContactTagConnectionV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              tagConnections: oldData.data.tagConnections.filter(
                (tag) => tag.id !== data.id,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toast.error("Error deleting contact tag connection");
    },

    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.contact, variables.contactId],
      });
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact tag connection deleted successfully");
      }
    },
  });
};
